import { useLocalState } from '@consigli/hooks';
import { flushSync } from 'react-dom';

import { prefersReducedMotion } from '@/util/prefers-reduced-motion';

export const PROJECT_VIEW = {
  GRID: 'grid',
  LIST: 'list',
} as const;

export type ProjectView = (typeof PROJECT_VIEW)[keyof typeof PROJECT_VIEW];

/** Namespaced; there is no existing convention beyond one ad hoc key. */
const STORAGE_KEY = 'project-list.view';

/**
 * Which layout the project list is showing, remembered across visits.
 *
 * Persisted because it is a preference rather than navigation state — someone
 * who chooses rows wants rows, and this is a page people return to constantly.
 * The creator filter deliberately is not: that answers a question, and silently
 * still applying it three visits later would show an incomplete list that looks
 * complete.
 */
export const useProjectView = (): [ProjectView, (view: ProjectView) => void] => {
  const [stored, setStored] = useLocalState<ProjectView>(STORAGE_KEY, PROJECT_VIEW.GRID);

  // useLocalState JSON.parses whatever is in storage and hands it back
  // unchecked, so a hand-edited key — or one written by a future third view —
  // would reach SegmentedControl as an unrecognised value and leave it with no
  // active segment, the one state it is built never to be in.
  const view: ProjectView = stored === PROJECT_VIEW.LIST ? PROJECT_VIEW.LIST : PROJECT_VIEW.GRID;

  // Each card morphs from its grid cell to its list row and back. See the
  // `project-card` view-transition class in index.css for the motion itself.
  const setView = (next: ProjectView) => {
    // Without the API, or with reduced motion asked for, the layout switches
    // at once, which is what it did before.
    if (typeof document.startViewTransition !== 'function' || prefersReducedMotion()) {
      setStored(next);
      return;
    }

    // flushSync commits the new layout inside the callback, which is when the
    // browser takes its "after" snapshot. React 18 would otherwise render it
    // after the callback returns, and the transition would animate nothing.
    //
    // Never the updater form: useLocalState types it as returning void, so the
    // stored value would come back undefined.
    document.startViewTransition(() => flushSync(() => setStored(next)));
  };

  return [view, setView];
};
